/**
 * useAgentSocket Hook
 * Connects to the Duckmon WebSocket server and provides real-time agent data.
 * When tokenAddress changes, emits 'token:analyze' to trigger server-side analysis.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { getSocket } from '../lib/websocket';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export interface AgentInfo {
    name: string;
    status: string;
    uptime: number;
    stats: Record<string, unknown>;
    lastHeartbeat: number;
    isAlive: boolean;
}

export interface TechnicalAnalysis {
    price: number;
    rsi: number;
    macd: { value: number; signal: number; histogram: number };
    bollinger: { upper: number; middle: number; lower: number; bandwidth: number; percentB: number };
    stochasticRSI: { k: number; d: number };
    trend: { direction: string; strength: number };
    momentum: number;
    volatility: number;
    atr: number;
    vwap: number;
    fibonacci: { levels: Array<{ ratio: number; label: string; price: number }>; high: number; low: number };
    ichimoku: { tenkan: number; kijun: number; senkouA: number; senkouB: number; signal: string };
    obv: number;
    fearGreed: number;
    regime: string;
    supportResistance: { support: number; resistance: number };
}

export interface AnalysisResult {
    tokenAddress: string;
    tokenSymbol: string;
    tokenName: string;
    timestamp: number;
    agentName: string;

    // Price data
    price: number;
    priceUsd: number;
    priceNative: number;
    priceChange24h: number;
    priceChange1h: number;
    priceChange5m: number;
    volume24h: number;
    liquidity: number;
    marketCap: number;
    buys24h: number;
    sells24h: number;
    buys1h: number;
    sells1h: number;

    // Technical analysis
    technical: TechnicalAnalysis | null;
    priceHistory: number[];

    // Signal
    type: 'BUY' | 'SELL' | 'HOLD';
    confidence: number;
    reasons: string[];
}

export interface AgentSignal {
    agentName: string;
    type: string;
    confidence: number;
    tokenAddress?: string;
    tokenSymbol?: string;
    reason?: string;
    price?: number;
    timestamp?: number;
    receivedAt: number;
}

export interface WhaleAlert {
    type: string;
    wallet?: string;
    amount?: number;
    impact?: string;
    sentiment?: string;
    receivedAt: number;
}

export interface GasUpdate {
    currentGas: number;
    recommendation?: string;
    prediction?: number;
    receivedAt: number;
}

export interface AgentSocketState {
    isConnected: boolean;
    agents: Record<string, AgentInfo>;
    analysisResult: AnalysisResult | null;
    recentSignals: AgentSignal[];
    whaleAlerts: WhaleAlert[];
    gasUpdates: GasUpdate[];
    requestAnalysis: (tokenAddress: string) => void;
}

// ═══════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════

export function useAgentSocket(tokenAddress?: string): AgentSocketState {
    const [isConnected, setIsConnected] = useState(false);
    const [agents, setAgents] = useState<Record<string, AgentInfo>>({});
    const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
    const [recentSignals, setRecentSignals] = useState<AgentSignal[]>([]);
    const [whaleAlerts, setWhaleAlerts] = useState<WhaleAlert[]>([]);
    const [gasUpdates, setGasUpdates] = useState<GasUpdate[]>([]);

    const lastRequestedToken = useRef<string | null>(null);

    // Request analysis for a specific token
    const requestAnalysis = useCallback((address: string) => {
        if (!address || address.length < 10) return;

        const socket = getSocket();
        if (socket.connected) {
            console.log(`[AgentSocket] Requesting analysis for ${address.slice(0, 10)}...`);
            socket.emit('token:analyze', { tokenAddress: address });
            lastRequestedToken.current = address.toLowerCase();
        }
    }, []);

    // Auto-request analysis when tokenAddress changes
    useEffect(() => {
        if (!tokenAddress) return;

        const key = tokenAddress.toLowerCase();
        if (key === lastRequestedToken.current) return;

        // Reset old analysis when token changes
        setAnalysisResult(null);
        requestAnalysis(tokenAddress);
    }, [tokenAddress, requestAnalysis]);

    // Socket connection and event listeners
    useEffect(() => {
        const socket = getSocket();

        // Connection state
        const onConnect = () => {
            setIsConnected(true);
            // Re-request analysis if we have a token
            if (tokenAddress && tokenAddress.length > 10) {
                requestAnalysis(tokenAddress);
            }
        };

        const onDisconnect = () => {
            setIsConnected(false);
        };

        // Server sends initial state on connect
        const onState = (data: {
            agents: Record<string, AgentInfo>;
            recentSignals: AgentSignal[];
            recentAlerts: WhaleAlert[];
        }) => {
            if (data.agents) setAgents(data.agents);
            if (data.recentSignals) setRecentSignals(data.recentSignals);
            if (data.recentAlerts) setWhaleAlerts(data.recentAlerts);
        };

        // Analysis result from server
        const onAnalysisResult = (result: AnalysisResult) => {
            setAnalysisResult(result);
        };

        // Agent heartbeats
        const onHeartbeat = (data: { agentName: string } & AgentInfo) => {
            setAgents(prev => ({
                ...prev,
                [data.agentName]: {
                    ...data,
                    name: data.agentName,
                    isAlive: true,
                },
            }));
        };

        // Agent signals
        const onSignal = (signal: AgentSignal) => {
            setRecentSignals(prev => [signal, ...prev].slice(0, 50));
        };

        // Whale alerts
        const onWhaleAlert = (alert: WhaleAlert) => {
            setWhaleAlerts(prev => [alert, ...prev].slice(0, 50));
        };

        // Gas updates
        const onGasUpdate = (update: GasUpdate) => {
            setGasUpdates(prev => [update, ...prev].slice(0, 20));
        };

        // Register listeners
        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        socket.on('state', onState);
        socket.on('analysis:result', onAnalysisResult);
        socket.on('agent:heartbeat', onHeartbeat);
        socket.on('signal', onSignal);
        socket.on('whale:alert', onWhaleAlert);
        socket.on('gas:update', onGasUpdate);

        // If already connected, trigger manually
        if (socket.connected) {
            setIsConnected(true);
        }

        return () => {
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
            socket.off('state', onState);
            socket.off('analysis:result', onAnalysisResult);
            socket.off('agent:heartbeat', onHeartbeat);
            socket.off('signal', onSignal);
            socket.off('whale:alert', onWhaleAlert);
            socket.off('gas:update', onGasUpdate);
        };
    }, [tokenAddress, requestAnalysis]);

    return {
        isConnected,
        agents,
        analysisResult,
        recentSignals,
        whaleAlerts,
        gasUpdates,
        requestAnalysis,
    };
}

export default useAgentSocket;
